import React from 'react';
import {
  TouchableOpacity,
  ScrollView,
  Modal,
  View,
  StyleSheet,
  Platform,
} from 'react-native';
import { useTranslation } from 'react-i18next';
import { Text, useThemeName } from '@tamagui/core';
import { YStack, XStack } from '@tamagui/stacks';
import {
  Settings,
  ChevronRight,
  User,
  FileText,
  List,
  Wallet,
  Info,
  Shield,
  FileCheck,
  CheckCircle,
  Phone,
  LogOut,
  Star,
} from 'lucide-react-native';

import type { TabScreenProps } from '@/types/navigation';
import {
  Screen,
  ScreenHeader,
  SvgBackground,
  AuthenticatedImage,
} from '@/components';
import { useAuth } from '@/contexts/AuthContext';
import { useLogout, useMyProfile } from '@/api';
import { useApplicationNotifications } from '@/hooks/useApplicationNotifications';
import { showSuccessToast, showErrorToast } from '@/utils/toast';
import { useIsMobileLayout } from '@/hooks/useWindowWidth';
import { COLORS } from '@/constants/colors';

type Props = TabScreenProps<'Settings'>;

const SettingsScreen: React.FC<Props> = ({ navigation }) => {
  const { t } = useTranslation();
  const themeName = useThemeName();
  const isMobileLayout = useIsMobileLayout();
  const isDark = themeName === 'dark';

  const bgColor = isDark ? COLORS.BLACK : '#f5f5f5';
  const cardBg = isDark ? '#1a1a1a' : COLORS.WHITE;
  const separatorColor =
    themeName === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.08)';
  const { logout: authLogout } = useAuth();
  const [showLogoutDialog, setShowLogoutDialog] = React.useState(false);

  const { data: profile } = useMyProfile();
  const { totalNewCount: newApplicationsCount } = useApplicationNotifications();

  const logout = useLogout({
    onSuccess: async () => {
      showSuccessToast(
        t('screens.settings.logoutSuccess', 'Logged out'),
        t(
          'screens.settings.logoutSuccessMessage',
          'You have been successfully logged out',
        ),
      );
      await authLogout();
    },
    onError: error => {
      showErrorToast(
        t('common.error', 'Error'),
        error.message || t('screens.settings.logoutError', 'Failed to logout'),
      );
    },
  });

  const confirmLogout = () => {
    logout.mutate();
    setShowLogoutDialog(false);
  };

  const getUserDisplayName = () => {
    if (profile?.nickName) return profile.nickName;
    if (profile?.firstName && profile?.lastName) {
      return `${profile.firstName} ${profile.lastName}`;
    }
    if (profile?.firstName) return profile.firstName;
    return t('screens.settings.guest', 'Guest');
  };

  const getUserPhoto = () => {
    if (!profile?.photos || profile.photos.length === 0) return null;
    const defaultPhoto =
      profile.photos.find(p => p.isDefault) || profile.photos[0];
    return defaultPhoto;
  };

  const accountSettingsItems = [
    {
      icon: User,
      title: t('screens.settings.myProfile', 'My Profile'),
      subtitle: t(
        'screens.settings.myProfileDesc',
        'View and edit your profile',
      ),
      hasChevron: true,
      onPress: () => (navigation as any).navigate('MyProfile'),
    },
    {
      icon: Phone,
      title: t('screens.settings.contactDetails', 'Contact Details'),
      subtitle: t(
        'screens.settings.contactDetailsDesc',
        'Manage your contact information',
      ),
      hasChevron: true,
      onPress: () => (navigation as any).navigate('ContactDetails'),
    },
    {
      icon: FileText,
      title: t('screens.settings.myApplications', 'My Applications'),
      subtitle: t(
        'screens.settings.myApplicationsDesc',
        'View your meeting applications',
      ),
      hasChevron: true,
      onPress: () => (navigation as any).navigate('MyApplications'),
    },
    {
      icon: List,
      title: t('screens.settings.myAnnouncements', 'My Announcements'),
      subtitle: t(
        'screens.settings.myAnnouncementsDesc',
        'Manage your meeting announcements',
      ),
      hasChevron: true,
      onPress: () => (navigation as any).navigate('MyAnnouncements'),
      badge: newApplicationsCount,
    },
    {
      icon: Wallet,
      title: t('screens.settings.wallet', 'Wallet'),
      subtitle: t(
        'screens.settings.walletDesc',
        'Manage your wallet and payments',
      ),
      hasChevron: true,
      onPress: () => (navigation as any).navigate('Wallet'),
    },
  ];

  const moreItems = [
    {
      icon: Info,
      title: t('screens.settings.about', 'About'),
      subtitle: t('screens.settings.aboutDesc', 'App information and version'),
      hasChevron: true,
      onPress: () => {},
    },
    {
      icon: Shield,
      title: t('screens.settings.privacy', 'Privacy Policy'),
      subtitle: t('screens.settings.privacyDesc', 'Read our privacy policy'),
      hasChevron: true,
      onPress: () => {},
    },
    {
      icon: FileCheck,
      title: t('screens.settings.terms', 'Terms of Service'),
      subtitle: t('screens.settings.termsDesc', 'Read our terms of service'),
      hasChevron: true,
      onPress: () => {},
    },
    {
      icon: LogOut,
      title: t('screens.settings.logout', 'Logout'),
      subtitle: t('screens.settings.logoutDesc', 'Sign out of your account'),
      hasChevron: false,
      onPress: () => setShowLogoutDialog(true),
    },
  ];

  const renderMenuItem = (
    item: (typeof accountSettingsItems)[0] & { badge?: number },
    index: number,
    isLast: boolean,
  ) => {
    const iconColor = themeName === 'dark' ? '#d4d4d8' : '#71717a';
    const chevronColor = themeName === 'dark' ? '#d4d4d8' : '#71717a';

    return (
      <TouchableOpacity
        key={index}
        onPress={item.onPress}
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          paddingVertical: 16,
          borderBottomWidth: isLast ? 0 : 1,
          borderBottomColor: separatorColor,
        }}
      >
        <YStack
          width={40}
          height={40}
          borderRadius={20}
          backgroundColor={themeName === 'dark' ? '#27272a' : '#f4f4f5'}
          alignItems="center"
          justifyContent="center"
          marginRight="$3"
        >
          <item.icon size={20} color={iconColor} />
        </YStack>
        <YStack flex={1}>
          <XStack alignItems="center" space="$2">
            <Text
              fontSize="$4"
              fontWeight="500"
              color={themeName === 'dark' ? '#ffffff' : '#18181b'}
            >
              {item.title}
            </Text>
            {item.badge !== undefined && item.badge > 0 && (
              <XStack
                backgroundColor={COLORS.ERROR}
                paddingHorizontal="$2"
                paddingVertical={2}
                borderRadius={10}
              >
                <Text fontSize={10} fontWeight="600" color="white">
                  {item.badge > 99 ? '99+' : item.badge}
                </Text>
              </XStack>
            )}
          </XStack>
          {item.subtitle && (
            <Text
              fontSize="$2"
              color={themeName === 'dark' ? '#a1a1aa' : '#71717a'}
              marginTop="$1"
            >
              {item.subtitle}
            </Text>
          )}
        </YStack>
        {item.hasChevron && <ChevronRight size={16} color={chevronColor} />}
      </TouchableOpacity>
    );
  };

  return (
    <Screen edges={['top']}>
      <YStack flex={1} backgroundColor={bgColor}>
        {/* Header */}
        {Platform.OS === 'web' && !isMobileLayout ? (
          <ScreenHeader
            title={t('screens.settings.title')}
            onBackPress={() => navigation.goBack()}
          />
        ) : (
          <SvgBackground
            offsetLeft={-30}
            offsetTop={-250}
            height={180}
            variant="flat"
            borderBottomLeftRadius={24}
            borderBottomRightRadius={24}
          >
            <XStack
              paddingTop="$10"
              paddingHorizontal="$4"
              paddingBottom="$3"
              alignItems="center"
              space="$3"
              width="100%"
            >
              <Settings size={24} color="white" />
              <Text
                fontSize={28}
                fontWeight="bold"
                color="white"
                fontFamily="Poppins"
                numberOfLines={1}
                adjustsFontSizeToFit
                minimumFontScale={0.8}
                flexShrink={1}
              >
                {t('screens.settings.title')}
              </Text>
            </XStack>
          </SvgBackground>
        )}

        <YStack flex={1}>
          <ScrollView
            contentContainerStyle={{ paddingTop: 16, paddingBottom: 100 }}
            showsVerticalScrollIndicator={false}
          >
            <YStack
              backgroundColor="$backgroundStrong"
              borderTopLeftRadius="$10"
              borderTopRightRadius="$10"
              paddingTop="$4"
              marginHorizontal="$3"
              borderWidth={themeName === 'dark' ? 0 : 1}
              borderColor={themeName === 'dark' ? 'transparent' : '#e5e7eb'}
            >
              <YStack paddingHorizontal="$4" paddingTop="$4" paddingBottom="$4">
                <XStack alignItems="center" space="$3">
                  <YStack
                    width={60}
                    height={60}
                    borderRadius={30}
                    backgroundColor="$gray4"
                    alignItems="center"
                    justifyContent="center"
                    overflow="hidden"
                  >
                    {getUserPhoto() ? (
                      <AuthenticatedImage
                        uri={getUserPhoto()!.mediumUrl}
                        style={{ width: 60, height: 60 }}
                        resizeMode="cover"
                      />
                    ) : (
                      <User
                        size={30}
                        color={themeName === 'dark' ? '#d4d4d8' : '#71717a'}
                      />
                    )}
                  </YStack>
                  <YStack flex={1}>
                    <XStack alignItems="center" space="$2">
                      <Text
                        fontSize="$5"
                        fontWeight="600"
                        color={themeName === 'dark' ? '#ffffff' : '#18181b'}
                      >
                        {getUserDisplayName()}
                      </Text>
                      {profile && <CheckCircle size={16} color="$green10" />}
                    </XStack>
                    <Text
                      fontSize="$3"
                      color={themeName === 'dark' ? '#a1a1aa' : '#71717a'}
                      marginTop="$1"
                    >
                      {profile?.email || ''}
                    </Text>
                    {profile && profile.ratingCount > 0 && (
                      <XStack alignItems="center" space="$1" marginTop="$1">
                        <Star size={14} color="#f59e0b" fill="#f59e0b" />
                        <Text
                          fontSize="$3"
                          color={themeName === 'dark' ? '#fbbf24' : '#d97706'}
                          fontWeight="500"
                        >
                          {profile.ratingAverage.toFixed(1)}
                        </Text>
                        <Text
                          fontSize="$2"
                          color={themeName === 'dark' ? '#a1a1aa' : '#71717a'}
                        >
                          (
                          {t('common.ratingCount', {
                            count: profile.ratingCount,
                          })}
                          )
                        </Text>
                      </XStack>
                    )}
                  </YStack>
                </XStack>
              </YStack>

              <YStack paddingHorizontal="$4" paddingTop="$4">
                <Text
                  fontSize="$3"
                  color={themeName === 'dark' ? '#a1a1aa' : '#71717a'}
                  marginBottom="$3"
                >
                  {t('screens.settings.accountSettings')}
                </Text>

                {renderMenuItem(
                  {
                    icon: Settings,
                    title: t('screens.settings.appSettings'),
                    subtitle: t('screens.settings.appSettingsDesc'),
                    hasChevron: true,
                    onPress: () =>
                      (navigation as any).navigate('ApplicationSettings'),
                  },
                  0,
                  false,
                )}

                {accountSettingsItems.map((item, index) =>
                  renderMenuItem(
                    item,
                    index + 1,
                    index === accountSettingsItems.length - 1,
                  ),
                )}
              </YStack>

              <YStack paddingHorizontal="$4" paddingTop="$6">
                <Text
                  fontSize="$3"
                  color={themeName === 'dark' ? '#a1a1aa' : '#71717a'}
                  marginBottom="$3"
                >
                  {t('screens.settings.more')}
                </Text>

                {moreItems.map((item, index) =>
                  renderMenuItem(item, index, index === moreItems.length - 1),
                )}
              </YStack>
            </YStack>
          </ScrollView>
        </YStack>

        <Modal
          visible={showLogoutDialog}
          transparent
          animationType="fade"
          onRequestClose={() => setShowLogoutDialog(false)}
        >
          <View style={styles.modalOverlay}>
            <YStack
              backgroundColor="$background"
              borderRadius="$4"
              padding="$5"
              minWidth={300}
              maxWidth="90%"
              space="$4"
              shadowColor="$shadowColor"
              shadowOffset={{ width: 0, height: 2 }}
              shadowOpacity={0.25}
              shadowRadius={3.84}
              elevation={5}
            >
              <Text fontSize="$6" fontWeight="600">
                {t('screens.settings.logoutTitle', 'Logout')}
              </Text>
              <Text fontSize="$4" color="$color10">
                {t(
                  'screens.settings.logoutMessage',
                  'Are you sure you want to logout?',
                )}
              </Text>

              <XStack space="$3" justifyContent="flex-end" marginTop="$2">
                <TouchableOpacity
                  onPress={() => setShowLogoutDialog(false)}
                  style={{ paddingVertical: 10, paddingHorizontal: 20 }}
                >
                  <Text fontSize="$4" color="$primary">
                    {t('common.cancel', 'Cancel')}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  onPress={confirmLogout}
                  disabled={logout.isPending}
                  style={{
                    paddingVertical: 10,
                    paddingHorizontal: 20,
                    backgroundColor: '#ef4444',
                    borderRadius: 8,
                  }}
                >
                  <Text fontSize="$4" color="white" fontWeight="600">
                    {logout.isPending
                      ? t('common.loading', 'Loading...')
                      : t('common.logout', 'Logout')}
                  </Text>
                </TouchableOpacity>
              </XStack>
            </YStack>
          </View>
        </Modal>
      </YStack>
    </Screen>
  );
};

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
});

export default SettingsScreen;
